import { ChevronDown, ShoppingBag } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useCatalogI18n } from "../i18n";
import { BG, BLUE, BORDER, GOLD, MUTED } from "../theme";
import type { Language } from "../../i18n/LanguageContext";
import type { Page } from "../types";

const LANGUAGE_FLAGS: Record<Language, string> = {
  ru: "🇷🇺",
  kz: "🇰🇿",
  en: "🇬🇧"
};

function LanguageSelector() {
  const { language, setLanguage, languages, copy } = useCatalogI18n();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) {
      return;
    }
    function handleClick(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div ref={containerRef} className="relative">
      <button
        onClick={() => setOpen((value) => !value)}
        className="flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[11px] uppercase transition-colors"
        style={{
          borderColor: BORDER,
          backgroundColor: "rgba(255,255,255,0.75)",
          color: MUTED,
          letterSpacing: "0.14em",
          fontWeight: 700
        }}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="text-sm leading-none">{LANGUAGE_FLAGS[language]}</span>
        <span>{copy.languageNames[language]}</span>
        <ChevronDown
          className="w-3.5 h-3.5 transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "none" }}
        />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute right-0 mt-2 min-w-[7rem] rounded-xl border shadow-lg overflow-hidden z-40"
          style={{ borderColor: BORDER, backgroundColor: "white" }}
        >
          {languages.map((languageCode) => {
            const isSelected = languageCode === language;
            return (
              <button
                key={languageCode}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  setLanguage(languageCode);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 px-3 py-2 text-[11px] uppercase transition-colors"
                style={{
                  backgroundColor: isSelected ? BLUE : "transparent",
                  color: isSelected ? "white" : MUTED,
                  letterSpacing: "0.14em",
                  fontWeight: 700
                }}
                onMouseEnter={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = BG;
                  }
                }}
                onMouseLeave={(event) => {
                  if (!isSelected) {
                    event.currentTarget.style.backgroundColor = "transparent";
                  }
                }}
              >
                <span className="text-sm leading-none">{LANGUAGE_FLAGS[languageCode]}</span>
                <span>{copy.languageNames[languageCode]}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

type CatalogHeaderProps = {
  page: Page;
  cartCount: number;
  onNavigate: (page: Page) => void;
  onOpenCart: () => void;
};

export function CatalogHeader({
  page,
  cartCount,
  onNavigate,
  onOpenCart
}: CatalogHeaderProps) {
  const { navItems } = useCatalogI18n();

  return (
    <header
      className="sticky top-0 z-30 backdrop-blur-md border-b"
      style={{ backgroundColor: "rgba(255,255,255,0.85)", borderColor: BORDER }}
    >
      <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
        <button
          onClick={() => onNavigate("home")}
          className="flex items-center text-2xl md:text-3xl"
          style={{ color: BLUE, fontWeight: 700, letterSpacing: "0.02em" }}
        >
          Baldai
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {navItems.map((item) => {
            const isActive = page === item.key;
            return (
              <button
                key={item.key}
                onClick={() => onNavigate(item.key)}
                className="relative text-sm uppercase transition-colors"
                style={{
                  color: isActive ? BLUE : MUTED,
                  fontWeight: isActive ? 600 : 500,
                  letterSpacing: "0.2em"
                }}
              >
                {item.label}
                {isActive && (
                  <span
                    className="absolute -bottom-1 left-0 right-0 h-[2px] rounded-full"
                    style={{ backgroundColor: GOLD }}
                  />
                )}
              </button>
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSelector />

          <button
            onClick={onOpenCart}
            className="relative p-2 rounded-full transition-colors"
            style={{ color: BLUE }}
            onMouseEnter={(event) => (event.currentTarget.style.backgroundColor = BG)}
            onMouseLeave={(event) =>
              (event.currentTarget.style.backgroundColor = "transparent")
            }
          >
            <ShoppingBag className="w-6 h-6" />
            {cartCount > 0 && (
              <span
                className="absolute -top-0.5 -right-0.5 w-5 h-5 rounded-full text-xs flex items-center justify-center text-white font-semibold"
                style={{ backgroundColor: GOLD }}
              >
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      <nav
        className="md:hidden flex justify-center gap-5 pb-3 border-t pt-3 flex-wrap"
        style={{ borderColor: BORDER }}
      >
        {navItems.map((item) => {
          const isActive = page === item.key;
          return (
            <button
              key={item.key}
              onClick={() => onNavigate(item.key)}
              className="text-xs uppercase"
              style={{
                color: isActive ? BLUE : MUTED,
                fontWeight: isActive ? 600 : 500,
                letterSpacing: "0.2em",
                borderBottom: isActive ? `2px solid ${GOLD}` : "2px solid transparent",
                paddingBottom: "2px"
              }}
            >
              {item.label}
            </button>
          );
        })}
      </nav>
    </header>
  );
}

export function CatalogFooter() {
  const { copy } = useCatalogI18n();

  return (
    <footer className="border-t py-8 text-center" style={{ borderColor: BORDER }}>
      <p className="text-sm" style={{ color: MUTED, letterSpacing: "0.05em" }}>
        <span style={{ color: BLUE, fontWeight: 600, letterSpacing: "0.15em" }}>BALDAI</span>
        <span className="mx-2" style={{ color: GOLD }}>
          ·
        </span>
        {copy.layout.footerTagline}
      </p>
    </footer>
  );
}
